<template>
  <div class="explore-page">
    <div class="global-grid"></div>
    <div class="ambient-glow"></div>
    <div class="explore-container">
      <header class="explore-header animate-box">
        <div class="h-left">
          <!-- 修改了Meta和标题文案 -->
          <span class="meta">AUTUMN_LOUNGE / 秋日客厅</span>
          <h1>在秋风停驻处，安放热爱</h1>
          <p>这里不只有代码，更有诗与远方。在来秋，我们用热爱对抗世界的荒芜。</p>
        </div>
        <div class="h-right">
          <div class="digital-sphere">
            <div class="core"></div>
            <div class="orbit o1"></div>
            <div class="orbit o2"></div>
          </div>
        </div>
      </header>

      <section class="tech-radar animate-box">
        <div class="section-title">
          <span class="decor"></span>
          <!-- 修改了板块标题 -->
          <h2>秋日兴趣图谱 / Interest Map</h2>
        </div>
        <div class="radar-grid">
          <div v-for="item in techSections" :key="item.title" class="radar-card hover-box">
            <div class="card-inner">
              <el-icon class="card-icon">
                <component :is="item.icon" />
              </el-icon>
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
              <div class="tags">
                <span v-for="tag in item.tags" :key="tag" class="tech-tag">{{ tag }}</span>
              </div>
              <div class="card-decor"></div>
            </div>
          </div>
        </div>
      </section>

      <section class="digital-emergence animate-box">
        <div class="emergence-wrapper">
          <div class="emergence-left">
            <!-- 修改了数据板块的标题和描述 -->
            <h3>秋日时光机</h3>
            <p>每一行代码都在记录，我们共同度过的温柔岁月。</p>
            <div class="code-stream">
              <div v-for="i in 5" :key="i" class="code-line">{{ generateCode() }}</div>
            </div>
          </div>
          <div class="emergence-right">
            <div v-for="stat in communityStats" :key="stat.label" class="stat-item">
              <div class="stat-value">{{ stat.value }}<span>{{ stat.unit }}</span></div>
              <div class="stat-label">{{ stat.label }}</div>
              <div class="stat-bar">
                <div class="fill" :style="{ width: stat.progress + '%' }"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div class="two-column-grid animate-box">
        <section class="pioneer-projects hover-box">
          <div class="section-title sub">
            <span class="decor"></span>
            <!-- 修改了项目板块标题 -->
            <h3>家园共建计划 / Our Projects</h3>
          </div>
          <div class="project-list">
            <div v-for="proj in projects" :key="proj.name" class="project-item">
              <div class="p-info">
                <h4>{{ proj.name }}</h4>
                <p>{{ proj.desc }}</p>
              </div>
              <div class="p-meta">
                <span class="version">{{ proj.version }}</span>
                <el-icon>
                  <Right />
                </el-icon>
              </div>
            </div>
          </div>
        </section>

        <section class="mind-collision hover-box">
          <div class="section-title sub">
            <span class="decor"></span>
            <!-- 修改了话题板块标题 -->
            <h3>茶余饭后 / Hot Topics</h3>
          </div>
          <div class="topic-tags">
            <div v-for="topic in hotTopics" :key="topic" class="topic-tag-item"> # {{ topic }} </div>
          </div>
          <div class="decor-grid-pattern"></div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, markRaw } from 'vue'
import { Monitor, Cpu, Star, Opportunity, Right } from '@element-plus/icons-vue'

// 1. 技术雷达数据 -> 修改为生活化内容
const techSections = ref([
  {
    title: '数字游民指南',
    desc: '记录代码之外的生活哲学，分享极客的诗意栖居。',
    icon: markRaw(Cpu),
    tags: ['极客生活', '慢节奏', '理想国']
  },
  {
    title: '生活美学实验室',
    desc: '从摄影构图到咖啡拉花，这里收藏生活的高光时刻。',
    icon: markRaw(Monitor),
    tags: ['摄影', '手冲咖啡', '阅读']
  },
  {
    title: '深夜树洞',
    desc: '卸下防备，与陌生人交换心事，这里是安全的避风港。',
    icon: markRaw(Star),
    tags: ['情感', '倾诉', '治愈']
  },
  {
    title: '未来生活提案',
    desc: '关于理想社区的构想，以及我们如何共建这个家园。',
    icon: markRaw(Opportunity),
    tags: ['社区共建', '线下聚会', '生活方式']
  }
])

// 2. 模拟社区统计数据 -> 修改为情感化数据
const communityStats = ref([
  { label: '温暖故事', value: '1.2', unit: 'K+', progress: 85 },
  { label: '同频伙伴', value: '8.9', unit: 'K+', progress: 92 },
  { label: '今日心动', value: '15', unit: 'M+', progress: 78 },
  { label: '深夜长谈', value: '340', unit: '场', progress: 65 }
])

// 3. 项目与话题数据 -> 修改为生活项目和话题
const projects = ref([
  {
    name: '秋日信使',
    desc: '连接彼此的温暖纽带，让每一次相遇都有回响。',
    version: 'v0.8.1-alpha'
  },
  {
    name: '时光胶囊',
    desc: '封存此刻的感动，留给未来的自己拆封。',
    version: 'v1.2.0-stable'
  },
  {
    name: '归宿契约',
    desc: '一份关于归属感的约定，证明你属于这里。',
    version: 'PoC_Stage'
  }
])

const hotTopics = ref([
  '秋天的第一杯奶茶喝了吗',
  '如何在城市里寻找旷野',
  '分享一首最近单曲循环的歌',
  '给陌生人的一封信',
  '周末去哪看海',
  '理想生活的样子'
])

// 辅助函数：生成模拟十六进制代码流 (保留原逻辑，文字内容不变，作为背景装饰)
const generateCode = () => {
  return `0x${Math.random().toString(16).substring(2, 10).toUpperCase()} >> [INIT_OK] - ${Math.random() > 0.5 ? 'DECODE' : 'SYNC'}`
}
</script>

<style scoped lang="scss">
// 引入硬核科技字体 (如果项目中未全局引入，需在此处引入，这里假设使用Inter)
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;800&family=JetBrains+Mono:wght@200&display=swap');

// 全局变量：高亮色（科技蓝）
:root {
  --accent: #409eff;
  --accent-glow: rgba(64, 158, 255, 0.3);
  --bg-dark: #050505;
  --card-bg: rgba(255, 255, 255, 0.03);
  --text-main: rgba(255, 255, 255, 0.9);
  --text-muted: rgba(255, 255, 255, 0.5);
}

.explore-page {
  background-color: #050505; // 极深背景
  color: rgba(255, 255, 255, 0.9);
  min-height: 100vh;
  font-family: 'Inter', sans-serif;
  position: relative;
  overflow-x: hidden;
  padding-top: 60px; // 模拟顶部导航预留
}

// 1. 全局装饰：极简网格和背景光晕
.global-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 80px 80px;
  background-position: center top;
  mask-image: radial-gradient(circle at center, black, transparent 90%);
  pointer-events: none;
}

.ambient-glow {
  position: absolute;
  top: 10%;
  left: 10%;
  width: 40%;
  height: 40%;
  background: radial-gradient(circle, rgba(64, 158, 255, 0.05) 0%, transparent 70%);
  filter: blur(80px);
  pointer-events: none;
}

// 通用容器
.explore-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 60px 100px 60px;
  position: relative;
  z-index: 2;
}

// 通用组件：板块标题
.section-title {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 50px;

  .decor {
    width: 30px;
    height: 1px;
    background: #409eff;
  }

  h2 {
    font-size: 1.8rem;
    font-weight: 800;
    letter-spacing: -1px;
  }

  &.sub h3 {
    font-size: 1.2rem;
    color: rgba(255, 255, 255, 0.8);
  }
}

// 动画和悬停效果通用基类
.animate-box {
  animation: fadeIn 0.8s ease-out;
}

.hover-box {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(255, 255, 255, 0.05);

  &:hover {
    border-color: rgba(64, 158, 255, 0.2);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3), 0 0 15px rgba(64, 158, 255, 0.1);
    background-color: rgba(255, 255, 255, 0.04) !important;
  }
}

// 1. 头部区域
.explore-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 120px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  padding-bottom: 60px;

  .h-left {
    max-width: 650px;

    .meta {
      font-size: 12px;
      color: #409eff;
      letter-spacing: 3px;
      font-weight: 600;
    }

    h1 {
      font-size: 3.5rem;
      font-weight: 800;
      margin: 20px 0;
      letter-spacing: -2px;
      line-height: 1.1;
    }

    p {
      font-size: 1.1rem;
      color: rgba(255, 255, 255, 0.6);
      line-height: 1.7;
    }
  }

  // 装饰性数字生命球
  .digital-sphere {
    position: relative;
    width: 200px;
    height: 200px;
    display: flex;
    justify-content: center;
    align-items: center;

    .core {
      width: 40px;
      height: 40px;
      background: #409eff;
      border-radius: 50%;
      box-shadow: 0 0 20px #409eff;
      animation: breathe 4s infinite;
    }

    .orbit {
      position: absolute;
      border-radius: 50%;
      border: 1px solid rgba(255, 255, 255, 0.08);
    }

    .o1 {
      width: 100px;
      height: 100px;
      border-style: dashed;
      animation: rotate 20s linear infinite;
    }

    .o2 {
      width: 160px;
      height: 160px;
      animation: rotate 30s linear infinite reverse;
    }
  }
}

// 2. 技术雷达
.radar-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 25px;
}

.radar-card {
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  overflow: hidden;
  position: relative;

  .card-inner {
    padding: 40px 30px;
    display: flex;
    flex-direction: column;
    height: 100%;

    .card-icon {
      font-size: 32px;
      color: #409eff;
      margin-bottom: 25px;
    }

    h3 {
      font-size: 1.3rem;
      font-weight: 600;
      margin: 0 0 15px 0;
    }

    p {
      font-size: 0.9rem;
      color: rgba(255, 255, 255, 0.5);
      line-height: 1.6;
      flex-grow: 1;
      margin-bottom: 25px;
    }

    .tags {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .tech-tag {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.6);
      background: rgba(255, 255, 255, 0.06);
      padding: 4px 10px;
      border-radius: 30px;
      border: 1px solid rgba(255, 255, 255, 0.03);
    }
  }

  // 悬停时的 0.5px 装饰线
  &:hover .card-decor {
    position: absolute;
    top: 15px;
    right: 15px;
    width: 10px;
    height: 10px;
    border-top: 1px solid rgba(255, 255, 255, 0.3);
    border-right: 1px solid rgba(255, 255, 255, 0.3);
  }
}

// 3. 数字涌现 (数据动态)
.digital-emergence {
  margin: 120px 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0) 100%);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 20px;
  overflow: hidden;
  backdrop-filter: blur(10px);

  .emergence-wrapper {
    display: flex;
    padding: 60px;
    gap: 80px;
    align-items: center;
  }

  .emergence-left {
    flex: 1;

    h3 {
      font-size: 1.8rem;
      font-weight: 700;
      margin: 0 0 10px 0;
    }

    p {
      color: rgba(255, 255, 255, 0.5);
      font-size: 0.95rem;
      margin-bottom: 30px;
    }

    // 代码流装饰 (等宽字体)
    .code-stream {
      font-family: 'JetBrains Mono', monospace;
      font-size: 11px;
      color: rgba(255, 255, 255, 0.15);
      line-height: 2;
      user-select: none;
    }
  }

  .emergence-right {
    flex: 1.5;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 30px 50px;
  }
}

.stat-item {
  .stat-value {
    font-size: 2.8rem;
    font-weight: 800;
    color: #fff;
    line-height: 1;

    span {
      font-size: 1rem;
      color: #409eff;
      font-weight: 400;
      margin-left: 5px;
    }
  }

  .stat-label {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.5);
    margin: 8px 0 15px 0;
  }

  .stat-bar {
    height: 2px;
    background: rgba(255, 255, 255, 0.08);
    position: relative;

    .fill {
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      background: #409eff;
      box-shadow: 0 0 5px #409eff;
      transition: width 1s ease-out;
    }
  }
}

// 4. 双列网格 (先锋计划 & 思维碰撞)
.two-column-grid {
  display: grid;
  grid-template-columns: 1.8fr 1.2fr;
  gap: 30px;
}

// 先锋计划
.pioneer-projects {
  background-color: rgba(255, 255, 255, 0.03);
  padding: 50px 40px;
  border-radius: 16px;

  .project-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .project-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-radius: 8px;
    background-color: rgba(255, 255, 255, 0.02);
    border: 1px solid transparent;
    transition: 0.3s;
    cursor: pointer;

    &:hover {
      border-color: rgba(255, 255, 255, 0.06);
      background-color: rgba(255, 255, 255, 0.04);

      .p-meta el-icon {
        color: #409eff;
        transform: translateX(3px);
      }
    }

    .p-info {
      max-width: 80%;

      h4 {
        font-size: 1.05rem;
        font-weight: 600;
        margin: 0 0 6px 0;
        font-family: 'JetBrains Mono';
        color: #fff;
      }

      p {
        font-size: 0.85rem;
        color: rgba(255, 255, 255, 0.5);
        margin: 0;
        line-height: 1.6;
      }
    }

    .p-meta {
      display: flex;
      align-items: center;
      gap: 15px;
      color: rgba(255, 255, 255, 0.3);
      font-size: 12px;

      .version {
        font-family: 'JetBrains Mono';
        background: rgba(64, 158, 255, 0.06);
        color: #409eff;
        padding: 2px 8px;
        border-radius: 4px;
      }

      el-icon {
        font-size: 16px;
        transition: 0.3s;
      }
    }
  }
}

// 思维碰撞
.mind-collision {
  background-color: rgba(255, 255, 255, 0.03);
  padding: 50px 40px;
  border-radius: 16px;
  position: relative;
  overflow: hidden;

  .topic-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    position: relative;
    z-index: 2;
  }

  .topic-tag-item {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.6);
    background: rgba(255, 255, 255, 0.06);
    padding: 8px 18px;
    border-radius: 4px;
    border: 1px solid rgba(255, 255, 255, 0.03);
    cursor: pointer;
    transition: 0.3s;

    &:hover {
      color: #fff;
      border-color: rgba(64, 158, 255, 0.2);
      background: rgba(64, 158, 255, 0.1);
    }
  }

  // 背景装饰网格
  .decor-grid-pattern {
    position: absolute;
    bottom: -30px;
    right: -30px;
    width: 150px;
    height: 150px;
    background-image: radial-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 1px);
    background-size: 15px 15px;
    opacity: 0.5;
  }
}

// 动画 keyframes
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes breathe {

  0%,
  100% {
    box-shadow: 0 0 20px #409eff;
    opacity: 1;
  }

  50% {
    box-shadow: 0 0 10px #409eff;
    opacity: 0.7;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

// 响应式适配
@media (max-width: 1200px) {
  .radar-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .two-column-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .explore-container {
    padding: 30px;
  }

  .explore-header {
    flex-direction: column;
    text-align: center;
    gap: 40px;

    h1 {
      font-size: 2.5rem;
    }
  }

  .radar-grid {
    grid-template-columns: 1fr;
  }

  .digital-emergence .emergence-wrapper {
    flex-direction: column;
    padding: 40px;
    gap: 40px;
  }

  .digital-emergence .emergence-right {
    grid-template-columns: 1fr;
    width: 100%;
  }
}
</style>